// [S101] Telemedicina Fase 1 — LISTA + "Nova teleconsulta".
// O médico pode ESCOLHER um paciente do prontuário (pré-preenche + vincula
// patientId → a Fase 2 anexa o relatório ao EHR) OU preencher MANUALMENTE
// (nome+telefone) pra quem ainda não tem prontuário. Recebe o link do paciente
// pra copiar/enviar e entra na sala. Gated por telemedicina_enabled (backend).
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Button, CircularProgress, Typography, Box, IconButton, Tooltip, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import VideoCallIcon from "@material-ui/icons/VideoCall";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CancelIcon from "@material-ui/icons/Cancel";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import {
  listTeleconsultas,
  createTeleconsulta,
  cancelTeleconsulta
} from "../../services/teleconsultaService";
import { listPatients } from "../../services/patientService";

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(2), overflowY: "auto", minHeight: 0 },
  center: { padding: theme.spacing(6), textAlign: "center", color: theme.palette.text.secondary },
  linkRow: { display: "flex", gap: 8, marginTop: theme.spacing(2), flexWrap: "wrap" },
  linkedHint: { display: "block", marginTop: theme.spacing(0.5), color: theme.palette.success.main }
}));

const STATUS = {
  waiting: { label: "Aguardando", color: "default" },
  active: { label: "Em consulta", color: "primary" },
  ended: { label: "Encerrada", color: "default" },
  cancelled: { label: "Cancelada", color: "secondary" }
};

const Teleconsultas = () => {
  const classes = useStyles();
  const history = useHistory();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patientName: "", patientPhone: "" });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { session, guestUrl }

  // Busca de paciente do prontuário (opcional)
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const debounceRef = useRef(null);

  // Modal de cancelamento (substitui o confirm() nativo)
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await listTeleconsultas();
      setRows(d.sessions || []);
    } catch (e) {
      const code = e && e.response && e.response.data && e.response.data.error;
      if (code === "ERR_TELE_DISABLED") toast.warn("Telemedicina não está habilitada para esta clínica.");
      else toast.error("Erro ao carregar teleconsultas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePatientInput = (term) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!term || term.trim().length < 2) { setPatientOptions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setPatientLoading(true);
      try {
        const data = await listPatients({ search: term, pageSize: 10 });
        setPatientOptions(Array.isArray(data && data.patients) ? data.patients : []);
      } catch (e) {
        setPatientOptions([]);
      } finally {
        setPatientLoading(false);
      }
    }, 400);
  };

  const closeDialog = () => {
    setOpen(false);
    setForm({ patientName: "", patientPhone: "" });
    setSelectedPatient(null);
    setPatientOptions([]);
    setCreated(null);
  };

  const handleCreate = async () => {
    if (!form.patientName.trim()) { toast.warn("Informe o nome do paciente."); return; }
    try {
      setCreating(true);
      const d = await createTeleconsulta({
        patientName: form.patientName.trim(),
        patientPhone: form.patientPhone.trim(),
        patientId: selectedPatient ? selectedPatient.id : undefined
      });
      setCreated(d);
      load();
    } catch (e) {
      const code = e && e.response && e.response.data && e.response.data.error;
      toast.error(code === "ERR_TELE_DISABLED" ? "Telemedicina não habilitada para esta clínica." : "Erro ao criar teleconsulta.");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (url) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => toast.success("Link copiado!")).catch(() => {});
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      setCancelling(true);
      await cancelTeleconsulta(cancelTarget);
      toast.success("Teleconsulta cancelada.");
      setCancelTarget(null);
      load();
    } catch (e) {
      toast.error("Erro ao cancelar.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Teleconsulta</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<VideoCallIcon />} onClick={() => setOpen(true)}>
            Nova teleconsulta
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <Box className={classes.center}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box className={classes.center}>
            <Typography>Nenhuma teleconsulta ainda. Clique em <strong>Nova teleconsulta</strong> para começar.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Paciente</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Criada</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const st = STATUS[r.status] || { label: r.status, color: "default" };
                const live = r.status === "waiting" || r.status === "active";
                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      {r.patientName}
                      {r.patientId ? <Chip size="small" label="prontuário" variant="outlined" style={{ marginLeft: 6 }} /> : null}
                    </TableCell>
                    <TableCell><Chip size="small" label={st.label} color={st.color} /></TableCell>
                    <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString("pt-BR") : "-"}</TableCell>
                    <TableCell align="right">
                      {live && (
                        <Tooltip title="Entrar na sala">
                          <IconButton size="small" color="primary" onClick={() => history.push(`/teleconsulta/${r.id}`)}>
                            <VideoCallIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {live && (
                        <Tooltip title="Cancelar">
                          <IconButton size="small" onClick={() => setCancelTarget(r.id)}>
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Nova teleconsulta */}
      <Dialog open={open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Nova teleconsulta</DialogTitle>
        <DialogContent>
          {!created ? (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Escolha um paciente do prontuário ou preencha manualmente.
              </Typography>
              <Autocomplete
                options={patientOptions}
                value={selectedPatient}
                loading={patientLoading}
                getOptionLabel={(o) => (o && o.fullName) || ""}
                getOptionSelected={(o, v) => o.id === (v && v.id)}
                onChange={(e, v) => {
                  setSelectedPatient(v);
                  if (v) setForm({ patientName: v.fullName || "", patientPhone: v.phone || "" });
                }}
                onInputChange={(e, v) => handlePatientInput(v)}
                noOptionsText="Digite ao menos 2 letras…"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Paciente do prontuário (opcional)"
                    margin="dense"
                    variant="outlined"
                    placeholder="Buscar por nome / CPF / telefone"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {patientLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
              <TextField
                margin="dense" label="Nome do paciente" fullWidth
                value={form.patientName}
                onChange={(e) => setForm({ ...form, patientName: e.target.value })}
              />
              <TextField
                margin="dense" label="Telefone (opcional)" fullWidth
                value={form.patientPhone}
                onChange={(e) => setForm({ ...form, patientPhone: e.target.value })}
              />
              {selectedPatient && (
                <Typography variant="caption" className={classes.linkedHint}>
                  ✓ Vinculada ao prontuário de {selectedPatient.fullName}.
                </Typography>
              )}
            </>
          ) : (
            <Box>
              <Typography gutterBottom>Sessão criada. Envie este link ao paciente:</Typography>
              <TextField
                value={created.guestUrl} fullWidth size="small"
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <Box className={classes.linkRow}>
                <Button variant="outlined" startIcon={<FileCopyIcon />} onClick={() => copyLink(created.guestUrl)}>
                  Copiar link
                </Button>
                <Button
                  variant="contained" color="primary" startIcon={<VideoCallIcon />}
                  onClick={() => history.push(`/teleconsulta/${created.session.id}`)}
                >
                  Entrar na sala
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{created ? "Fechar" : "Cancelar"}</Button>
          {!created && (
            <Button variant="contained" color="primary" onClick={handleCreate} disabled={creating}>
              {creating ? "Criando…" : "Criar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmação de cancelamento (modal — substitui o confirm() nativo) */}
      <Dialog open={!!cancelTarget} onClose={() => (cancelling ? null : setCancelTarget(null))} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar teleconsulta?</DialogTitle>
        <DialogContent>
          <Typography color="textSecondary">
            A sala é encerrada e o link do paciente deixa de funcionar. Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling}>Voltar</Button>
          <Button color="secondary" variant="contained" onClick={confirmCancel} disabled={cancelling}>
            {cancelling ? "Cancelando…" : "Cancelar teleconsulta"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Teleconsultas;
